import React from 'react'
import Login from './components/auth/Login'
import Layout from './components/layout/Layout'
import NotFound from './components/layout/errors/NotFound'
import Unauthorized from './components/layout/errors/Unauthorized'
import HelpPages from './components/help/index'
import { RequireAuth, SessionLockScreen, SessionExpiryWarning, useAuth } from './auth'
import { apiClient } from './auth/services/apiClient'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import routesConfig, { ROLES } from './routeConfig'
import ForgotPassword from './components/auth/ForgotPassword'
import AppInitiatedDialog from './components/common/dialogs/AppInitiatedDialog'
import UpdatePassword from './components/auth/UpdatePassword'
import { useAppDispatch } from './store/hooks'
import { getLocalStorageValue } from './components/common/utility/helpers'
import { changeTheme } from './features/theme/theme.slice'
import {
    changeTopNav,
    setDashboardChart,
    toggleSideNav,
} from './features/settings/settings.slice'

const App: React.FC = () => {
    const dispatch = useAppDispatch()
    const settings = getLocalStorageValue('settings', {})
    const { isAuthenticated, isLoading, isAppInitiated } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        apiClient.setOnForbidden(() => navigate('/unauthorized', { replace: true }))
    }, [navigate])

    useEffect(() => {
        dispatch(setDashboardChart(settings.dashboard || {}))
        dispatch(toggleSideNav(settings.sideNavTpggled))
        dispatch(changeTopNav(settings.topNavFixed))
        dispatch(changeTheme(settings.theme || 'light'))
    }, [
        dispatch,
        settings.sideNavTpggled,
        settings.topNavFixed,
        settings.theme,
    ])

    if (isLoading) return null

    return (
        <>
            {!isAppInitiated ? (
                <AppInitiatedDialog />
            ) : (
                <>
                    <SessionLockScreen />
                    <SessionExpiryWarning />

                    <Routes>
                        <Route
                            path="login"
                            element={
                                isAuthenticated ? (
                                    <Navigate to={'/'} />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                        <Route
                            path="forgot-password"
                            element={<ForgotPassword />}
                        />
                        <Route
                            path="update-password/:token"
                            element={<UpdatePassword />}
                        />
                        <Route
                            path="unauthorized"
                            element={<Unauthorized />}
                        />

                        <Route
                            element={
                                <RequireAuth
                                    allowedRoles={[
                                        ROLES.Admin,
                                        ROLES.Editor,
                                        ROLES.User,
                                        ROLES.Owner,
                                    ]}
                                />
                            }
                        >
                            <Route
                                path="help/*"
                                element={<HelpPages.HelpHome />}
                            />
                            <Route path="/" element={<Layout />}>
                                {routesConfig.map((route, i) => {
                                    if (!route?.static === true) {
                                        return route.protected ? (
                                            <Route
                                                key={i}
                                                element={
                                                    <RequireAuth
                                                        allowedRoles={[
                                                            ...route.roles,
                                                            ROLES.Owner,
                                                        ]}
                                                    />
                                                }
                                            >
                                                <Route
                                                    path={route.path}
                                                    element={
                                                        React.createElement(route.component!)
                                                    }
                                                />
                                            </Route>
                                        ) : (
                                            <Route
                                                key={i}
                                                element={React.createElement(route.component!)}
                                            />
                                        )
                                    }

                                    return ''
                                })}
                            </Route>
                        </Route>

                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </>
            )}
        </>
    )
}

export default App
